/**
 * Componente Button
 *
 * Botão reutilizável com variantes e tamanhos.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { forwardRef, ButtonHTMLAttributes } from 'react';
import { Spinner } from '../Spinner';

/**
 * Variantes do botão
 */
type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';

/**
 * Tamanhos do botão
 */
type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * Props do componente Button
 */
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

/**
 * Classes CSS por variante
 */
const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-primary-800 text-white hover:bg-primary-900 focus:ring-primary-500 disabled:bg-primary-400',
  secondary:
    // Texto branco: contraste AA no normal (5,7:1) e no hover (7,1:1); texto escuro sobre roxo ficava abaixo de 3:1
    'bg-secondary-700 text-white hover:bg-secondary-800 focus:ring-secondary-500 disabled:bg-secondary-300 disabled:text-secondary-900',
  outline:
    'border-2 border-primary-800 text-primary-800 hover:bg-primary-50 focus:ring-primary-500 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20',
  ghost:
    'text-primary-800 hover:bg-primary-100 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-800/20',
  danger:
    'bg-error-600 text-white hover:bg-error-700 focus:ring-error-500 disabled:bg-error-400',
};

/**
 * Classes CSS por tamanho
 */
const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm gap-1.5',
  md: 'px-4 py-2 text-base gap-2',
  lg: 'px-6 py-3 text-lg gap-2.5',
};

/**
 * Componente Button com forwardRef
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      className = '',
      children,
      ...props
    },
    ref,
  ) => {
    const baseClasses =
      'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed';

    const classes = [
      baseClasses,
      variantClasses[variant],
      sizeClasses[size],
      fullWidth ? 'w-full' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={classes}
        {...props}
      >
        {isLoading ? (
          <Spinner size="sm" color={variant === 'primary' || variant === 'danger' ? 'white' : 'primary'} />
        ) : (
          <>
            {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';
